import { useState } from "react";
import { fmt } from "../../formatters";
import { FONT, COLORS } from "../../styles";

const FLAG_OPTIONS = [
  { value: "auto", label: "Auto", color: COLORS.dim },
  { value: "revenue", label: "Revenue", color: COLORS.green },
  { value: "not_revenue", label: "Not Revenue", color: COLORS.orange },
  { value: "lender", label: "Lender", color: COLORS.red },
  { value: "exclude", label: "Exclude", color: COLORS.dim },
];

const flagColors = { auto: COLORS.dim, revenue: COLORS.green, not_revenue: COLORS.orange, lender: COLORS.red, exclude: COLORS.dim };

export default function TransactionsTab({ transactions, onFlagChange, merchantId }) {
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [flagging, setFlagging] = useState(null);

  const filtered = transactions
    .filter((t) => { if (filter === "credits") return t.type === "credit"; if (filter === "debits") return t.type === "debit"; if (filter === "loans") return t.isLoan || t.flag === "lender"; if (filter === "flagged") return t.flag && t.flag !== "auto"; if (filter === "excluded") return t.flag === "exclude"; return true; })
    .filter((t) => !searchTerm || t.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleFlag = async (txn, newFlag) => {
    if (!onFlagChange || !merchantId) return;
    const key = txn.txn_key || `${txn.date}|${txn.name}|${txn.amount}`;
    setFlagging(key);
    try { await onFlagChange(merchantId, key, newFlag); } catch (err) { console.error("Flag error:", err); } finally { setFlagging(null); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
        <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search transactions..."
          style={{ flex: 1, minWidth: 200, background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "8px 14px", fontSize: 13, color: COLORS.text, outline: "none", fontFamily: FONT }} />
        {["all", "credits", "debits", "loans", "flagged", "excluded"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            style={{ background: filter === f ? COLORS.blueLight : COLORS.card, border: `1px solid ${filter === f ? COLORS.primary : COLORS.border}`, borderRadius: 8, padding: "6px 14px", fontSize: 12, color: filter === f ? COLORS.primary : COLORS.muted, cursor: "pointer", textTransform: "capitalize", fontFamily: FONT, fontWeight: filter === f ? 600 : 400 }}>
            {f}
          </button>
        ))}
      </div>

      <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
        <div style={{ display: "grid", gridTemplateColumns: "90px 1fr 100px 100px 80px 110px", padding: "10px 16px", borderBottom: `1px solid ${COLORS.border}`, background: COLORS.bg }}>
          {["Date", "Description", "Amount", "Balance", "Auto Tag", "Flag"].map((h) => (
            <span key={h} style={{ fontSize: 11, color: COLORS.dim, fontWeight: 600, fontFamily: FONT }}>{h}</span>
          ))}
        </div>
        <div style={{ maxHeight: 600, overflowY: "auto" }}>
          {filtered.slice(0, 150).map((t, i) => {
            const txnKey = t.txn_key || `${t.date}|${t.name}|${t.amount}`;
            const currentFlag = t.flag || "auto";
            const isExcluded = currentFlag === "exclude";
            const isFlagged = currentFlag !== "auto";
            const isUpdating = flagging === txnKey;
            return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "90px 1fr 100px 100px 80px 110px", padding: "8px 16px", borderBottom: `1px solid ${COLORS.borderLight}`, background: isExcluded ? COLORS.bg : (t.isLoan || currentFlag === "lender") ? COLORS.redLight : i % 2 === 0 ? "transparent" : COLORS.bg, opacity: isExcluded ? 0.4 : 1, transition: "opacity 0.2s" }}>
                <span style={{ fontSize: 12, color: COLORS.dim, fontFamily: FONT }}>{t.date}</span>
                <span style={{ fontSize: 12, color: (t.isLoan || currentFlag === "lender") ? COLORS.red : currentFlag === "revenue" ? COLORS.green : COLORS.text, fontFamily: FONT, textDecoration: isExcluded ? "line-through" : "none" }}>{t.name}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: t.type === "credit" ? COLORS.green : COLORS.orange, fontFamily: FONT }}>{t.type === "credit" ? "+" : ""}{fmt(t.amount)}</span>
                <span style={{ fontSize: 12, color: t.balance < 5000 ? COLORS.red : t.balance < 15000 ? COLORS.orange : COLORS.muted, fontFamily: FONT }}>{fmt(t.balance)}</span>
                <span>
                  {(t.isLoan && currentFlag === "auto") && <span style={{ fontSize: 10, background: COLORS.redLight, color: COLORS.red, padding: "2px 6px", borderRadius: 4, fontWeight: 500 }}>{t.type === "credit" ? "ADV" : "FAC"}</span>}
                  {t.balance < 5000 && !t.isLoan && currentFlag === "auto" && <span style={{ fontSize: 10, background: COLORS.redLight, color: COLORS.red, padding: "2px 6px", borderRadius: 4, fontWeight: 500 }}>LOW</span>}
                </span>
                <select value={currentFlag} onChange={(e) => handleFlag(t, e.target.value)} disabled={isUpdating}
                  style={{ background: isFlagged ? COLORS.bg : COLORS.card, border: `1px solid ${isFlagged ? flagColors[currentFlag] + "44" : COLORS.border}`, borderRadius: 6, padding: "3px 6px", fontSize: 11, color: flagColors[currentFlag], fontFamily: FONT, cursor: isUpdating ? "wait" : "pointer", outline: "none", width: "100%", opacity: isUpdating ? 0.5 : 1 }}>
                  {FLAG_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                </select>
              </div>
            );
          })}
        </div>
        <div style={{ padding: "10px 16px", borderTop: `1px solid ${COLORS.border}`, fontSize: 12, color: COLORS.dim, fontFamily: FONT, display: "flex", justifyContent: "space-between" }}>
          <span>Showing {Math.min(filtered.length, 150)} of {filtered.length} transactions</span>
          <span>{transactions.filter((t) => t.flag && t.flag !== "auto").length} flagged</span>
        </div>
      </div>
    </div>
  );
}
